// AdminEventsTab.tsx — onglet « Événements » de la fiche créateur (2026-10-17)
//
// Le dossier du créateur : notes des super-admins (« Ajouter un événement ») et
// traces écrites d'office quand un super-admin éteint, verrouille ou déverrouille
// une de ses chaînes depuis le Dashboard. Liste du plus récent au plus ancien ; le
// bouton « Détail » montre qui est à l'origine de l'événement, la date et l'heure,
// et le commentaire entier. Pour un ajout, l'auteur (le compte connecté) et la date
// sont remplis d'office ; seule la description est à saisir, et elle est obligatoire.
// Données : admin-api AdminEventController (api_adminEvents.ts).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import styles from './AdminEventsTab.module.scss';

import { useTypedSelector } from '../../../store/useTypeSelector';
import { RootState        } from '../../../store/generalReducer';
import { AdminEvent, getAdminEvents, addAdminEvent } from '../../../backendServices/adminManagment/api_adminEvents';

interface Props {
  creatorId   : number;
  creatorName : string;
}

type Filter = 'all' | 'note' | 'channel_lock' | 'channel_unlock' | 'channel_stop' | 'video_disable' | 'video_enable';

export const KIND_LABEL: Record<string, string> = {
  note           : 'Note',
  channel_lock   : 'Chaîne verrouillée',
  channel_unlock : 'Chaîne déverrouillée',
  channel_stop   : 'Chaîne éteinte',
  video_disable  : 'Vidéo désactivée',
  video_enable   : 'Vidéo rétablie',
};
const KIND_CLASS: Record<string, string> = {
  note           : styles.kNote,
  channel_lock   : styles.kLock,
  channel_unlock : styles.kUnlock,
  channel_stop   : styles.kStop,
  video_disable  : styles.kLock,
  video_enable   : styles.kUnlock,
};
const COMMENT_MAX = 5000;   // comme AdminEventController::COMMENT_MAX

const dateTime = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const shortDateTime = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const Kind: React.FC<{ kind: string }> = ({ kind }) => (
  <span className={`${styles.kind} ${KIND_CLASS[kind] ?? styles.kNote}`}>{KIND_LABEL[kind] ?? kind}</span>
);

/** Fenêtre modale simple : fond qui ferme, Échap qui ferme, focus posé à l'ouverture. */
const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode; busy?: boolean }> = ({ title, onClose, children, busy }) => {
  const box = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', esc);
    box.current?.focus();
    return () => document.removeEventListener('keydown', esc);
  }, [onClose, busy]);
  return (
    <div className={styles.overlay} onMouseDown={() => !busy && onClose()}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={box} onMouseDown={e => e.stopPropagation()}>
        <div className={styles.modalHead}>
          <h3>{title}</h3>
          <button type="button" className={styles.close} aria-label="Fermer" disabled={busy} onClick={onClose}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
};

const AdminEventsTab: React.FC<Props> = ({ creatorId, creatorName }) => {
  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);
  const me = authentication.admin;
  const myName = (me?.pseudonym || `${me?.firstname ?? ''} ${me?.lastname ?? ''}`.trim() || me?.email || 'Compte inconnu');

  const [ events , setEvents  ] = useState<AdminEvent[]>([]);
  const [ loading, setLoading ] = useState(true);
  const [ error  , setError   ] = useState('');
  const [ filter , setFilter  ] = useState<Filter>('all');
  const [ detail , setDetail  ] = useState<AdminEvent | null>(null);
  const [ adding , setAdding  ] = useState<{ at: Date } | null>(null);
  const [ text   , setText    ] = useState('');
  const [ saving , setSaving  ] = useState(false);
  const [ saveErr, setSaveErr ] = useState('');

  const load = useCallback(async () => {
    if (!me?.id) { setError('Compte non identifié : reconnectez-vous.'); setLoading(false); return; }
    setLoading(true);
    const res = await getAdminEvents(creatorId, me.id);
    if (res.ok) { setEvents(res.data); setError(''); } else setError(res.error);
    setLoading(false);
  }, [creatorId, me?.id]);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => events.filter(e => filter === 'all' || e.kind === filter), [events, filter]);
  const count = (k: Filter) => k === 'all' ? events.length : events.filter(e => e.kind === k).length;

  const openAdd = () => { setAdding({ at: new Date() }); setText(''); setSaveErr(''); };
  const save = async () => {
    if (!me?.id || text.trim() === '') return;
    setSaving(true); setSaveErr('');
    const res = await addAdminEvent(creatorId, me.id, text.trim());
    setSaving(false);
    if (!res.ok) { setSaveErr(res.error); return; }
    setEvents(list => [res.data, ...list]);
    setAdding(null);
  };

  return (
    <div className={styles.root}>
      <div className={styles.head}>
        <div className={styles.titleBlock}>
          <h2 className={styles.title}>Événements</h2>
          <span className={styles.subtitle}>Le dossier de {creatorName} : notes de l'équipe et actions sur ses chaînes, du plus récent au plus ancien.</span>
        </div>
        <button type="button" className={styles.primary} onClick={openAdd}>+ Ajouter un événement</button>
      </div>

      <div className={styles.filters} role="group" aria-label="Type d'événement">
        {(['all', 'note', 'channel_lock', 'channel_unlock', 'channel_stop', 'video_disable', 'video_enable'] as Filter[]).map(k => (
          <button key={k} type="button" aria-pressed={filter === k} className={filter === k ? styles.filterOn : styles.filter} onClick={() => setFilter(k)}>
            {k === 'all' ? 'Tous' : KIND_LABEL[k]} <span className={styles.count}>{count(k)}</span>
          </button>
        ))}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {!error && loading && <div className={styles.empty}>Chargement…</div>}

      {!error && !loading && shown.length === 0 && (
        <div className={styles.empty}>
          {events.length === 0
            ? <>Aucun événement pour ce créateur. Les actions du Dashboard sur ses chaînes s'ajouteront ici d'elles-mêmes ; « Ajouter un événement » permet de noter le reste (échange, avertissement…).</>
            : <>Aucun événement de ce type.</>}
        </div>
      )}

      {!error && shown.length > 0 && (
        <div className={styles.tableBox}>
          <table className={styles.table}>
            <thead>
              <tr><th>Date et heure</th><th>Type</th><th>À l'origine</th><th>Chaîne / vidéo</th><th>Commentaire</th><th aria-label="Actions"></th></tr>
            </thead>
            <tbody>
              {shown.map(e => (
                <tr key={e.id}>
                  <td className={styles.nowrap}>{shortDateTime(e.at)}</td>
                  <td><Kind kind={e.kind} /></td>
                  <td className={styles.nowrap}>{e.author}</td>
                  <td className={styles.channel}>{e.videoName ? `Vidéo « ${e.videoName} »` : (e.channelName ?? '—')}</td>
                  <td className={styles.comment} title={e.comment}>{e.comment.split('\n')[0]}</td>
                  <td className={styles.right}><button type="button" className={styles.secondary} onClick={() => setDetail(e)}>Détail</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Détail ── */}
      {detail && (
        <Modal title="Détail de l'événement" onClose={() => setDetail(null)}>
          <dl className={styles.facts}>
            <div><dt>Type</dt><dd><Kind kind={detail.kind} /></dd></div>
            <div><dt>À l'origine</dt><dd>{detail.author}</dd></div>
            <div><dt>Date et heure</dt><dd>{dateTime(detail.at)}</dd></div>
            {detail.ip && <div><dt>Adresse IP</dt><dd>{detail.ip}</dd></div>}
            {detail.channelName && <div><dt>Chaîne</dt><dd>{detail.channelName}</dd></div>}
            {detail.videoName && <div><dt>Vidéo</dt><dd>n° {detail.videoId} — « {detail.videoName} »</dd></div>}
          </dl>
          <div className={styles.label}>Commentaire</div>
          <div className={styles.fullComment}>{detail.comment}</div>
          <div className={styles.actions}>
            <button type="button" className={styles.secondary} onClick={() => setDetail(null)}>Fermer</button>
          </div>
        </Modal>
      )}

      {/* ── Ajouter un événement ── */}
      {adding && (
        <Modal title="Ajouter un événement" onClose={() => setAdding(null)} busy={saving}>
          <div className={styles.formRow}>
            <label className={styles.label} htmlFor="admin-event-author">À l'origine de l'événement</label>
            <input id="admin-event-author" className={styles.readonly} value={myName} readOnly />
          </div>
          <div className={styles.formRow}>
            <label className={styles.label} htmlFor="admin-event-date">Date et heure</label>
            <input id="admin-event-date" className={styles.readonly} value={dateTime(adding.at.toISOString())} readOnly />
            <span className={styles.hint}>Enregistrées par le serveur au moment de l'ajout.</span>
          </div>
          <div className={styles.formRow}>
            <label className={styles.label} htmlFor="admin-event-text">Description <strong>(obligatoire)</strong></label>
            <textarea id="admin-event-text" className={styles.textarea} rows={6} maxLength={COMMENT_MAX} value={text} required
                      aria-invalid={text.trim() === ''} autoFocus
                      placeholder="Par exemple : appel du créateur au sujet du verrouillage de sa chaîne, il s'engage à retirer les contenus en cause."
                      onChange={e => setText(e.target.value)} />
            <span className={styles.hint}>{text.length} / {COMMENT_MAX}</span>
          </div>
          {saveErr && <div className={styles.error}>{saveErr}</div>}
          <div className={styles.actions}>
            <button type="button" className={styles.secondary} disabled={saving} onClick={() => setAdding(null)}>Annuler</button>
            <button type="button" className={styles.primary} disabled={saving || text.trim() === ''} onClick={save}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdminEventsTab;
